import React, { useMemo, useState, useCallback } from "react";
import "./ReportScreen.css";
import {
  useModerationWindowSession,
  SEASONS,
  MODERATION_TYPE,
  MODERATION_TYPE_LABELS,
} from "../../constants/Constants";
import { useApi } from "../../context/ApiClient";
import PropTypes from "prop-types";

const buildReportsList = (visits = [], subjectName = "") => {
  return visits
    .filter(
      (visit) =>
        visit.visitDate &&
        visit.stageId &&
        typeof visit.reportPath === "string" &&
        visit.reportPath.trim() !== "",
    )
    .map((visit) => {
      const units = (visit.assessments || []).map(
        (assessment) => assessment.split(":")[0],
      );
      const moderationWindowSession = useModerationWindowSession();
      const visitDateObj = new Date(visit.visitDate);

      let dateToken;

      if (visit.stageId === 4) {
        const dateSource =
          visit.stageId === 4 && visit.additionalVisitDate
            ? visit.additionalVisitDate
            : visit.visitDate;

        const visitDateObj = new Date(dateSource);

        dateToken = isNaN(visitDateObj)
          ? ""
          : `${String(visitDateObj.getDate()).padStart(2, "0")}${String(
              visitDateObj.getMonth() + 1,
            ).padStart(2, "0")}${String(visitDateObj.getFullYear()).slice(-2)}`;
      } else {
        dateToken = isNaN(visitDateObj)
          ? ""
          : `${String(visitDateObj.getDate()).padStart(2, "0")}${String(
              visitDateObj.getMonth() + 1,
            ).padStart(2, "0")}${String(visitDateObj.getFullYear()).slice(-2)}`;
      }

      const reportName =
        visit.stageId === 4 ? "AdditionalModerationReport" : "ModerationReport";

      const subjectToken = subjectName.replace(/[^A-Za-z0-9]+/g, "");
      const fileName = `${visit.centreNumber}_${subjectToken}_${reportName}_${dateToken}.pdf`;

      // Academic year
      let academicYear = "";
      if (visit.sessionStartDate && visit.sessionEndDate) {
        const startY = new Date(visit.sessionStartDate).getFullYear();
        const endY = new Date(visit.sessionEndDate).getFullYear();
        if (!isNaN(startY) && !isNaN(endY)) {
          academicYear = `${startY}/${String(endY).slice(-2)}`;
        }
      }
      if (!academicYear && Number.isInteger(visit.sessionYear)) {
        academicYear = `${visit.sessionYear}/${String(
          visit.sessionYear + 1,
        ).slice(-2)}`;
      }
      if (!academicYear) {
        const currentYear = new Date().getFullYear();
        academicYear = `${currentYear}/${String(currentYear + 1).slice(-2)}`;
      }

      // Window label
      let windowNumber = 1;
      let season = SEASONS.SPRING;
      if (visit.sessionId === moderationWindowSession.window1) {
        windowNumber = 1;
        season = SEASONS.SPRING;
      } else if (visit.sessionId === moderationWindowSession.window2) {
        windowNumber = 2;
        season = SEASONS.SUMMER;
      }
      const windowLabel = `Window ${windowNumber} (${season}) – ${academicYear}`;

      // Moderation type display
      const visitType =
        visit.moderationType === MODERATION_TYPE.FACE_TO_FACE
          ? MODERATION_TYPE_LABELS[MODERATION_TYPE.FACE_TO_FACE]
          : MODERATION_TYPE_LABELS[visit.moderationType] ||
            visit.moderationType;

      return {
        visitArrangementId: visit.visitArrangementId,
        visitDate: visit.visitDate,
        units,
        window: windowLabel,
        visitType,
        fileName,
        reportUrl: visit.reportPath,
        raw: visit,
      };
    })
    .sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate));
};

const formatDate = (dateStr) => {
  if (!dateStr) return ""; // or return "N/A", depending on what you want

  const date = new Date(dateStr);
  if (isNaN(date)) return ""; // handle invalid date strings

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ReportScreen = ({ reportData }) => {
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadErrorId, setDownloadErrorId] = useState(null);
  const api = useApi(process.env.REACT_APP_VAF_BASE_URL);
  const moderationWindowSession = useModerationWindowSession();

  const reports = useMemo(() => {
    if (!reportData) return [];
    // If reportsList already exists, just recompute window label
    if (
      Array.isArray(reportData.reportsList) &&
      reportData.reportsList.length > 0
    ) {
      return reportData.reportsList.map((row) => {
        if (!row.raw) return row;
        const visit = row.raw;

        let academicYear = "";
        if (visit.sessionStartDate && visit.sessionEndDate) {
          const startY = new Date(visit.sessionStartDate).getFullYear();
          const endY = new Date(visit.sessionEndDate).getFullYear();
          if (!isNaN(startY) && !isNaN(endY)) {
            academicYear = `${startY}/${String(endY).slice(-2)}`;
          }
        }
        if (!academicYear && Number.isInteger(visit.sessionYear)) {
          academicYear = `${visit.sessionYear}/${String(
            visit.sessionYear + 1,
          ).slice(-2)}`;
        }
        if (!academicYear) {
          const currentYear = new Date().getFullYear();
          academicYear = `${currentYear}/${String(currentYear + 1).slice(-2)}`;
        }

        let windowNumber = 1;
        let season = SEASONS.SPRING;
        if (visit.sessionId === moderationWindowSession.window1) {
          windowNumber = 1;
          season = SEASONS.SPRING;
        } else if (visit.sessionId === moderationWindowSession.window2) {
          windowNumber = 2;
          season = SEASONS.SUMMER;
        }
        const windowLabel = `Window ${windowNumber} (${season}) – ${academicYear}`;

        return { ...row, window: windowLabel };
      });
    }
    const visits = reportData.visitArrangementResponse || [];
    return buildReportsList(visits, reportData.selectedSubject || "");
  }, [reportData]);

  // Blob download approach .
  const handleDownload = useCallback(async (report) => {
    if (!report?.reportUrl || !report?.raw?.stageId) return;

    setDownloadingId(report.visitArrangementId);
    setDownloadErrorId(null);

    try {
      const stageId = report.raw.stageId;
      const visitArrangementId = report.visitArrangementId;

      // FIX: sanitize any stray quotes and trim path before encoding
      const cleanPath = report.reportUrl.trim().replace(/^"+|"+$/g, "");
      const encodedPath = encodeURIComponent(cleanPath);

      const endpoint = `${process.env.REACT_APP_VAF_BASE_URL}downloadReport?reportPath=${encodedPath}&stageId=${stageId}&visitArrangementId=${visitArrangementId}`;

      // Allow 303; fetch blob
      const response = await api.get(endpoint, {
        responseType: "blob",
        validateStatus: (status) => status >= 200 && status < 400,
      });

      // Filename from Content-Disposition (fallback to generated one)
      let filename = report.fileName || "moderation-report.pdf";
      const cd = response.headers["content-disposition"];
      if (cd) {
        const match = cd.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
    } catch (err) {
      setDownloadErrorId(report.visitArrangementId);

      // Auto-clear the error message after 5 seconds (without changing other UI)
      setTimeout(() => {
        // Clear only if it's still the same row showing error
        setDownloadErrorId((current) =>
          current === report.visitArrangementId ? null : current,
        );
      }, 5000);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  // Early return AFTER hooks → safe
  if (reports.length === 0) {
    return (
      <div className="container">
        <div className="notification no-reports left-align">
          There are no reports available yet.
        </div>
      </div>
    );
  }

  const formatVisitDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return isNaN(date)
      ? ""
      : date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  };

  return (
    <div className="container">
      <div className="reports-container">
        {reports.map((report) => {
          const hasError = downloadErrorId === report.visitArrangementId;

          return (
            <div className="report-row" key={report.visitArrangementId}>
              <div className="is-flex">
                <div className="is-flex-grow-1">
                  <strong className="report-visit-date left-align">
                    Visit date: {formatVisitDate(report.visitDate)}
                    {report.raw?.additionalVisitDate && (
                      <div className="is-flex is-align-items-center">
                        <span className="icon mr-1">
                          <svg
                            style={{ fill: "#a55900" }}
                            xmlns="http://www.w3.org/2000/svg"
                            height="24px"
                            viewBox="0 -960 960 960"
                            width="24px"
                            fill="212121"
                          >
                            <path d="m560-120-57-57 144-143H200v-480h80v400h367L503-544l56-57 241 241-240 240Z" />
                          </svg>
                        </span>
                        <span className="pt-1">
                          <strong style={{ color: "#a55900" }}>
                            {`Additional visit: ${formatDate(
                              report.raw.additionalVisitDate,
                            )}`}
                          </strong>
                        </span>
                      </div>
                    )}
                  </strong>
                  <div className="report-tags-wrapper mt-0">
                    <span className="report-tag">
                      Units: {report.units.join(", ")}
                    </span>
                    <span className="report-tag">{report.window}</span>
                    <span className="report-tag">{report.visitType}</span>
                  </div>
                </div>
                <div className="report-actions">
                  <button
                    className={`button report mr-2 ${
                      report.reportUrl ? "is-primary" : "is-outlined"
                    }`}
                    type="button"
                    // keep button interactive even while download is happening — stable look & behaviour
                    disabled={!report.reportUrl}
                    onClick={() => handleDownload(report)}
                    title={
                      report.reportUrl
                        ? "Download report (PDF)"
                        : "Report not available yet"
                    }
                  >
                    <span className="icon mr-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                        fill="#FFFFFF"
                      >
                        <path d="M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z"></path>
                      </svg>
                    </span>
                    Download report (PDF)
                  </button>

                  {hasError && (
                    <span
                      style={{
                        color: "#DD1F36",
                        fontSize: "12px",
                        marginLeft: "6px",
                      }}
                    >
                      Failed. Retry later.
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportScreen;

ReportScreen.propTypes = {
  reportData: PropTypes.shape({
    reportsList: PropTypes.arrayOf(
      PropTypes.shape({
        raw: PropTypes.shape({
          sessionStartDate: PropTypes.string,
          sessionEndDate: PropTypes.string,
          sessionYear: PropTypes.number,
          sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          stageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        reportUrl: PropTypes.string,
        fileName: PropTypes.string,
        visitArrangementId: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
      }),
    ),

    visitArrangementResponse: PropTypes.array, // refine if needed
    selectedSubject: PropTypes.string,
  }),
};
 